import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Stack, Warning, ClockCounterClockwise, UploadSimple } from '@phosphor-icons/react';
import { apiLaminas, parsearMarca } from '@/api/laminas';
import type { Hoja, MarcaData } from '@/api/laminas';
import { leerVistaCompartida, limpiarEnlaceDeLaUrl } from '@/components/laminas/compartir';
import type { VistaCompartida } from '@/components/laminas/compartir';
import { VisorLamina } from '@/components/laminas/VisorLamina';
import { PanelSubida } from '@/components/laminas/PanelSubida';
import { NavegadorLaminas } from '@/components/laminas/NavegadorLaminas';
import { useAppStore } from '@/store/appStore';

// Pantalla de la exploración de láminas (réplica del motor de planos de
// Fieldwire). Dos estados: la GRILLA de láminas y el VISOR de una lámina.
//
// Aislada del editor vectorial de /planta: no importa nada de
// `components/planta/` ni de `api/client.ts`. Si se descarta, se borran esta
// página, `components/laminas/`, `api/laminas.ts`, la línea del `<Route>` y la
// del NavMenu.
//
// NOTA HONESTA: todavía no existe el ingestor (PDF → tiles). Hasta que exista,
// la grilla va a venir vacía aunque el script 029 esté aplicado. La pantalla lo
// dice en vez de parecer rota.

export function LaminasPage() {
  // F-7: el enlace compartido se lee UNA sola vez, al montar, y de
  // `window.location` en vez del router. Es la misma decisión de aislamiento
  // que el cliente propio: esta pantalla no se engancha a nada de la app que
  // haya que desconectar el día que la exploración se descarte.
  const enlace = useMemo(() => leerVistaCompartida(window.location.search), []);
  const [planoAbierto, setPlanoAbierto] = useState<number | null>(enlace?.idPlano ?? null);

  // Abrir otra lámina o volver a la grilla deja el enlace viejo sin sentido: si
  // no se limpia, recargar la página vuelve a la lámina del enlace y parece que
  // la navegación no hubiera pasado.
  const ir = (id: number | null) => {
    limpiarEnlaceDeLaUrl();
    setPlanoAbierto(id);
  };

  if (planoAbierto !== null) {
    return (
      // `key` por lámina: saltar a otra desmonta el visor y con él todo su
      // estado local (hoja elegida, marca seleccionada, herramienta). Sin esto
      // el `idHoja` de la lámina anterior sobrevive al salto y el visor pide una
      // hoja que ya no pertenece al plano abierto.
      <Visor
        key={planoAbierto}
        idPlano={planoAbierto}
        // Solo vale para la lámina que el enlace nombró: al saltar a otra, el
        // encuadre guardado no se corresponde con ningún papel.
        enlace={enlace?.idPlano === planoAbierto ? enlace : null}
        onVolver={() => ir(null)}
        onAbrir={ir}
      />
    );
  }
  return <Grilla onAbrir={ir} />;
}

// ── Grilla ──────────────────────────────────────────────────────────────────

function Grilla({ onAbrir }: { onAbrir: (id: number) => void }) {
  const [subiendo, setSubiendo] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['laminas'],
    queryFn: () => apiLaminas.lista(),
  });

  // Agrupadas por carpeta, como ARQ/EST/MEC en Fieldwire. Sin carpeta van al
  // final bajo "Sin carpeta" y no se esconden.
  const porCarpeta = useMemo(() => {
    const mapa = new Map<string, typeof data>();
    for (const p of data || []) {
      const k = p.carpeta || 'Sin carpeta';
      if (!mapa.has(k)) mapa.set(k, []);
      mapa.get(k)!.push(p);
    }
    return [...mapa.entries()].sort(([a], [b]) =>
      a === 'Sin carpeta' ? 1 : b === 'Sin carpeta' ? -1 : a.localeCompare(b));
  }, [data]);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mb-5 flex items-center gap-2">
        <Stack size={20} weight="bold" className="text-brand-gray-500" />
        <h1 className="text-xl font-semibold text-brand-gray-500">Láminas</h1>
        <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] font-medium text-brand-gray-400">
          exploración
        </span>
        <button
          type="button"
          onClick={() => setSubiendo(v => !v)}
          className="ml-auto flex items-center gap-1.5 rounded bg-brand-green-100 px-3 py-1.5 text-sm font-semibold text-black hover:bg-brand-green-200"
        >
          <UploadSimple size={15} weight="bold" /> Subir láminas
        </button>
      </div>

      {subiendo && <PanelSubida onCerrar={() => setSubiendo(false)} />}

      {isLoading && <p className="text-sm text-brand-gray-400">Cargando láminas…</p>}

      {error && (
        <Aviso
          titulo="El endpoint de láminas no contestó"
          detalle={(error as Error).message}
          pista="Si dice «Invalid object name 'LamPlanos'», falta aplicar backend/sql/029_laminas_exploracion.sql contra la base."
        />
      )}

      {!isLoading && !error && !(data || []).length && (
        <Aviso
          titulo="Todavía no hay láminas"
          detalle="Las tablas responden, pero están vacías."
          pista="Soltá un PDF en «Subir láminas». Cada página se archiva como una lámina, con el nombre leído del cajetín."
        />
      )}

      {porCarpeta.map(([carpeta, planos]) => (
        <section key={carpeta} className="mb-7">
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-brand-gray-400">
            {carpeta} · {planos!.length}
          </h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3">
            {planos!.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => onAbrir(p.id)}
                className="group overflow-hidden rounded-lg border border-brand-gray-200 bg-white text-left transition-shadow hover:shadow-md"
              >
                <div className="flex h-[135px] items-center justify-center bg-brand-gray-100">
                  {p.urlMiniatura
                    ? <img src={p.urlMiniatura} alt={p.nombre} className="h-full w-full object-contain" />
                    : <span className="text-[11px] text-brand-gray-400">sin miniatura</span>}
                </div>
                <div className="p-2.5">
                  <div className="flex items-start gap-1.5">
                    <p className="flex-1 truncate text-sm font-semibold text-brand-gray-500">{p.nombre}</p>
                    {!p.nombreConfirmado && (
                      // El nombre lo propuso el OCR del rótulo y nadie lo
                      // revisó: marcarlo es lo que evita que un plano quede
                      // archivado con un nombre inventado.
                      <span title="Nombre propuesto automáticamente, sin revisar"
                        className="rounded bg-brand-yellow/20 px-1 text-[10px] font-bold text-brand-gray-500">?</span>
                    )}
                  </div>
                  {p.versiones > 1 && (
                    <p className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-brand-gray-400">
                      <ClockCounterClockwise size={11} weight="bold" />
                      {p.versiones} versiones
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Visor ───────────────────────────────────────────────────────────────────

function Visor({ idPlano, enlace, onVolver, onAbrir }: {
  idPlano: number;
  /** El encuadre y las capas que trajo un enlace compartido (F-7). */
  enlace: VistaCompartida | null;
  onVolver: () => void;
  /** Saltar a otra lámina del juego sin pasar por la grilla. */
  onAbrir: (id: number) => void;
}) {
  const { data: plano, isLoading } = useQuery({
    queryKey: ['laminas', idPlano],
    queryFn: () => apiLaminas.plano(idPlano),
  });

  // F-8. El único punto donde láminas toca el estado global de la app, y es de
  // lectura: el TaskModal ya está montado en App.tsx y solo hay que decirle cuál
  // tarea. Nada que desarmar si la exploración se descarta.
  const selectTask = useAppStore(s => s.selectTask);

  // El enlace puede nombrar una VERSIÓN concreta, no solo la lámina: comparar
  // un detalle contra la v1 y mandar el enlace tiene que abrir la v1.
  const [idHoja, setIdHoja] = useState<number | null>(enlace?.idHoja ?? null);
  // La vigente es la de versión más alta; las hojas ya vienen ordenadas así.
  const hojaElegida: Hoja | undefined =
    plano?.hojas.find(h => h.id === idHoja) || plano?.hojas[0];

  const clave = ['laminas', 'hoja', hojaElegida?.id];
  const { data: capas } = useQuery({
    queryKey: clave,
    queryFn: () => apiLaminas.hoja(hojaElegida!.id),
    enabled: !!hojaElegida,
  });

  const qc = useQueryClient();
  // Al terminar se refresca la hoja entera en vez de insertar la marca a mano:
  // el id y la fecha los pone el servidor, y una lista de marcas no es cara.
  const refrescar = () => qc.invalidateQueries({ queryKey: clave });

  // `privada` decide en qué CAPA cae la marca (F-5): personal la ve solo su
  // autor, publicada la ve el equipo. El servidor ya filtra las privadas ajenas
  // al leer, así que acá solo hay que no perder el flag.
  const crear = useMutation({
    mutationFn: (v: { data: MarcaData; privada: boolean }) =>
      apiLaminas.crearMarca(hojaElegida!.id, v.data, v.privada),
    onSuccess: refrescar,
  });

  const borrar = useMutation({
    mutationFn: (id: number) => apiLaminas.borrarMarca(id),
    onSuccess: refrescar,
  });

  // Mover, repintar, reescribir o cambiar de capa: los cuatro mandan el Feature
  // entero. La marca ES su GeoJSON, así que no hay un PATCH por campo que valga
  // la pena. `privada` viaja obligatoria porque el PUT reescribe la fila: el
  // visor manda la capa actual de la marca cuando lo que cambia es otra cosa.
  const actualizar = useMutation({
    mutationFn: (v: { id: number; data: MarcaData; privada: boolean }) =>
      apiLaminas.actualizarMarca(v.id, v.data, v.privada),
    onSuccess: refrescar,
  });

  // Los vínculos cuelgan de la HOJA (como las marcas), así que refrescan con
  // `clave`. El chinche de tarea, en cambio, cuelga del plano.
  const vincular = useMutation({
    mutationFn: (v: { posX: number; posY: number; planoDestinoId: number; descripcion: string }) =>
      apiLaminas.crearVinculo(hojaElegida!.id, { ...v, urlAdjunto: null, color: null }),
    onSuccess: refrescar,
  });

  const desvincular = useMutation({
    mutationFn: (id: number) => apiLaminas.borrarVinculo(id),
    onSuccess: refrescar,
  });

  // Las fotos cuelgan del PLANO (como los chinches), así que refrescan la
  // consulta del plano y no la de la hoja: invalidar `clave` las dejaría sin
  // aparecer hasta cambiar de versión.
  const refrescarPlano = () => qc.invalidateQueries({ queryKey: ['laminas', idPlano] });

  const subirFoto = useMutation({
    mutationFn: (v: { punto: { posX: number; posY: number }; archivo: File }) =>
      apiLaminas.subirFoto(idPlano, v.archivo, v.punto),
    onSuccess: refrescarPlano,
  });

  const quitarFoto = useMutation({
    mutationFn: (id: number) => apiLaminas.borrarFoto(id),
    onSuccess: refrescarPlano,
  });

  // F-10. Las anotaciones sobre la foto viajan COMPLETAS y no de a una: ver el
  // encabezado de `031_lamfotos_marcas.sql`. Por eso es una sola mutación con
  // el dibujo entero y no el trío crear/actualizar/borrar que tienen las marcas
  // del plano.
  const anotarFoto = useMutation({
    mutationFn: (v: { idFoto: number; marcas: MarcaData[] }) =>
      apiLaminas.guardarMarcasFoto(v.idFoto, v.marcas),
    onSuccess: refrescarPlano,
  });

  // F-8. El chinche cuelga del PLANO (igual que la foto), así que invalida
  // `['laminas', idPlano]` y NO la clave de la hoja: con `clave` el chinche
  // recién clavado no aparecería hasta cambiar de versión.
  const clavarChinche = useMutation({
    mutationFn: (c: { posX: number; posY: number; tareaId: number; titulo: string }) =>
      // `estado` y `color` van en null a propósito: el estado que pinta el
      // chinche se RESUELVE contra la tarea viva, no se copia acá (una copia
      // miente en cuanto alguien mueva la tarea en el kanban). `posZ` es 0:
      // existe por simetría con Fieldwire, que la trae y tampoco la usa.
      apiLaminas.crearChinche(idPlano, {
        posX: c.posX, posY: c.posY, posZ: 0,
        titulo: c.titulo, color: null, prioridad: 0, estado: null,
        tareaId: c.tareaId,
      }),
    onSuccess: refrescarPlano,
  });

  const despinchar = useMutation({
    mutationFn: (id: number) => apiLaminas.borrarChinche(id),
    onSuccess: refrescarPlano,
  });

  // Calibrar vive en `plano`, no en `clave`: `metrosPorPixel` viaja en la hoja
  // del detalle del plano, que es de donde sale `hojaElegida`. Invalidar solo la
  // hoja dejaría el visor mostrando «sin calibrar» después de calibrar.
  const calibrar = useMutation({
    mutationFn: (v: { px: number; metros: number | null }) =>
      apiLaminas.calibrar(hojaElegida!.id, v.px, v.metros),
    onSuccess: refrescarPlano,
  });

  // F-7. Publicar en lote: N marcas personales pasan a la capa del equipo.
  //
  // Son N PUTs y no un endpoint nuevo A PROPÓSITO: el dato ya existe y el
  // endpoint ya sabe cambiar la capa de una marca (es lo mismo que hace el
  // selector de la barra desde F-5). Un `POST /publicar-todas` agregaría una
  // `[Function]` nueva —y con eso un Stop+Start del Function App— para ahorrar
  // unas pocas requests que se disparan una vez por sesión de trabajo.
  //
  // SECUENCIAL, no en paralelo: el PUT reescribe la fila entera, y si una falla
  // se quiere saber cuántas alcanzaron a pasar en vez de dejar un lote a medias
  // sin orden conocido.
  const publicarPersonales = useMutation({
    mutationFn: async (ids: number[]) => {
      const vivas = capas?.marcas || [];
      for (const id of ids) {
        const m = vivas.find(x => x.id === id);
        if (!m || !m.privada) continue;
        const d = parsearMarca(m);
        // Una marca con `data` ilegible se saltea en vez de volar el lote: el
        // resto se publica igual y el GeoJSON roto es un problema aparte.
        if (!d) continue;
        await apiLaminas.actualizarMarca(id, d, false);
      }
    },
    onSuccess: refrescar,
  });

  const fallo = (crear.error || borrar.error || actualizar.error || calibrar.error
    || vincular.error || desvincular.error
    || clavarChinche.error || despinchar.error
    || subirFoto.error || quitarFoto.error || anotarFoto.error
    || publicarPersonales.error) as Error | null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-brand-gray-200 bg-white px-4 py-2.5">
        <button type="button" onClick={onVolver}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-sm text-brand-gray-500 hover:bg-brand-gray-100">
          <ArrowLeft size={15} weight="bold" /> Láminas
        </button>
        <span className="truncate text-sm font-semibold text-brand-gray-500">{plano?.nombre || '…'}</span>

        {/* Navegar el juego: anterior/siguiente, salto por nombre y flechas del
            teclado. Va acá y no dentro del visor porque lo que cambia es QUÉ
            lámina está abierta, y eso lo decide esta página. */}
        <div className="ml-auto flex items-center gap-2">
          <NavegadorLaminas idActual={idPlano} onAbrir={onAbrir} />

        {/* Selector de versión. Cambiar de versión cambia las MARCAS (cuelgan
            de la hoja) pero NO los chinches (cuelgan del plano). */}
        {!!plano?.hojas.length && plano.hojas.length > 1 && (
          <select
            value={hojaElegida?.id ?? ''}
            onChange={e => setIdHoja(Number(e.target.value))}
            className="rounded border border-brand-gray-300 px-2 py-1 text-xs text-brand-gray-500"
          >
            {plano.hojas.map(h => (
              <option key={h.id} value={h.id}>
                v{h.version}{h.nombreArchivo ? ` · ${h.nombreArchivo}` : ''}
              </option>
            ))}
          </select>
        )}
        </div>
      </div>

      <div className="min-h-0 flex-1 p-3">
        {isLoading && <p className="text-sm text-brand-gray-400">Cargando…</p>}
        {!isLoading && !hojaElegida && (
          <Aviso titulo="Esta lámina no tiene ninguna versión subida"
            detalle="El plano existe pero no hay hojas." />
        )}
        {fallo && (
          // Si el POST falla, la marca dibujada desaparece al refrescar. Decirlo
          // es lo que evita que alguien crea que anotó y siga trabajando.
          <p className="mb-2 rounded border border-brand-red-100 bg-brand-red-100/10 px-3 py-1.5 text-xs text-brand-red-200">
            No se pudo guardar: {fallo.message}
          </p>
        )}
        {hojaElegida && (
          <VisorLamina
            hoja={hojaElegida}
            // Todas las versiones: el visor las ofrece como candidatas a
            // comparar (F-6). La hoja comparada la carga él, por la misma
            // clave de caché que usa esta página.
            hojasVersion={plano?.hojas || []}
            marcas={capas?.marcas || []}
            chinches={plano?.chinches || []}
            vinculos={capas?.vinculos || []}
            fotos={plano?.fotos || []}
            idPlano={idPlano}
            // Para el nombre del PNG y el título de la impresión (F-7).
            nombreLamina={plano?.nombre || 'lamina'}
            // Ya viene memoizado desde `LaminasPage`: entra en las dependencias
            // del efecto que restaura el encuadre.
            vistaInicial={enlace}
            onPublicarPersonales={ids => publicarPersonales.mutate(ids)}
            publicando={publicarPersonales.isPending}
            onCrear={(data, privada) => crear.mutate({ data, privada })}
            onBorrar={id => borrar.mutate(id)}
            onActualizar={(id, data, privada) => actualizar.mutate({ id, data, privada })}
            onCalibrar={(px, metros) => calibrar.mutate({ px, metros })}
            onIrALamina={onAbrir}
            onCrearVinculo={v => vincular.mutate(v)}
            onBorrarVinculo={id => desvincular.mutate(id)}
            onSubirFoto={(punto, archivo) => subirFoto.mutate({ punto, archivo })}
            onBorrarFoto={id => quitarFoto.mutate(id)}
            onGuardarMarcasFoto={(idFoto, marcas) => anotarFoto.mutate({ idFoto, marcas })}
            guardandoMarcasFoto={anotarFoto.isPending}
            onCrearChinche={c => clavarChinche.mutate(c)}
            onBorrarChinche={id => despinchar.mutate(id)}
            // El puente completo: tocar el chinche abre el MISMO TaskModal que
            // el kanban. Está montado global en App.tsx, así que acá no hay más
            // que avisarle al store cuál tarea.
            onAbrirTarea={selectTask}
            guardando={crear.isPending || borrar.isPending || actualizar.isPending
              || calibrar.isPending || vincular.isPending || desvincular.isPending
              || clavarChinche.isPending || despinchar.isPending
              || subirFoto.isPending || quitarFoto.isPending || anotarFoto.isPending}
          />
        )}
      </div>
    </div>
  );
}

function Aviso({ titulo, detalle, pista }: { titulo: string; detalle?: string; pista?: string }) {
  return (
    <div className="max-w-xl rounded-lg border border-brand-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Warning size={16} weight="bold" className="text-brand-yellow" />
        <p className="text-sm font-semibold text-brand-gray-500">{titulo}</p>
      </div>
      {detalle && <p className="mt-1.5 text-xs text-brand-gray-400">{detalle}</p>}
      {pista && <p className="mt-2 border-t border-brand-gray-100 pt-2 text-xs text-brand-gray-400">{pista}</p>}
    </div>
  );
}
